import type { SkinTranslucencySetting } from "../rendering/skin_runtime";

export interface InvivoMenuHandle {
  dispose(): void;
}

export interface InvivoMenuAnatomyNode {
  id: string;
  label: string;
  meshes: string[];
  ownMeshes?: string[];
  visible: boolean;
  variant?: boolean;
  defaultVariant?: boolean;
  complication?: boolean | string | null;
  children: InvivoMenuAnatomyNode[];
}

type MenuView =
  | { kind: "anatomy"; node: InvivoMenuAnatomyNode | null }
  | { kind: "skin" };

const SKIN_TRANSLUCENCY_OPTIONS: Array<{
  setting: SkinTranslucencySetting;
  label: string;
}> = [
  { setting: "opaque", label: "Opaque" },
  { setting: "translucent", label: "Translucent" },
  { setting: "outlines", label: "Outlines" },
];

function getSkinLabel(setting: SkinTranslucencySetting): string {
  return SKIN_TRANSLUCENCY_OPTIONS.find((option) => option.setting === setting)?.label || setting;
}

function forEachNode(nodes: InvivoMenuAnatomyNode[], visitor: (node: InvivoMenuAnatomyNode) => void): void {
  nodes.forEach((node) => {
    visitor(node);
    forEachNode(node.children, visitor);
  });
}

function findPath(
  nodes: InvivoMenuAnatomyNode[],
  target: InvivoMenuAnatomyNode | null,
  path: InvivoMenuAnatomyNode[] = []
): InvivoMenuAnatomyNode[] {
  if (!target) {
    return [];
  }

  for (const node of nodes) {
    const nextPath = [...path, node];

    if (node.id === target.id) {
      return nextPath;
    }

    const childPath = findPath(node.children, target, nextPath);

    if (childPath.length) {
      return childPath;
    }
  }

  return [];
}

export function createInvivoMenu({
  canvas,
  sceneTitle = "Anatomy",
  anatomyTree = [],
  anatomyGroups,
  initialSkinSetting,
  initialDrapesVisible = true,
  onSkinChange,
  onDrapesVisibleChange,
  onAnatomyNodeVisibleChange,
  onAnatomyGroupVisibleChange,
}: {
  canvas: HTMLCanvasElement;
  sceneTitle?: string;
  anatomyTree?: InvivoMenuAnatomyNode[];
  anatomyGroups?: InvivoMenuAnatomyNode[];
  initialSkinSetting: SkinTranslucencySetting;
  initialDrapesVisible?: boolean;
  onSkinChange(setting: SkinTranslucencySetting): void;
  onDrapesVisibleChange(visible: boolean): void;
  onAnatomyNodeVisibleChange?(id: string, visible: boolean): void;
  onAnatomyGroupVisibleChange?(id: string, visible: boolean): void;
}): InvivoMenuHandle {
  const parent = canvas.parentElement;

  if (!parent) {
    return {
      dispose(): void {},
    };
  }

  const tree = anatomyTree.length ? anatomyTree : anatomyGroups || [];
  const visibility = new Map<string, boolean>();
  const activeViews: MenuView[] = [{ kind: "anatomy", node: null }];
  let skinSetting = initialSkinSetting;
  let lastVisibleSkinSetting: SkinTranslucencySetting = initialSkinSetting === "none" ? "opaque" : initialSkinSetting;
  let drapesVisible = initialDrapesVisible;
  let isOpen = false;

  forEachNode(tree, (node) => {
    visibility.set(node.id, node.visible);
  });

  const shell = document.createElement("div");
  shell.className = "invivo-menu";

  const sheet = document.createElement("section");
  sheet.className = "invivo-menu__sheet";
  sheet.setAttribute("aria-label", "Anatomy layers");
  sheet.hidden = true;

  const bottomBar = document.createElement("nav");
  bottomBar.className = "invivo-menu__bottom-bar";
  bottomBar.setAttribute("aria-label", "Viewer actions");

  const anatomyAction = document.createElement("button");
  anatomyAction.type = "button";
  anatomyAction.className = "invivo-menu__nav-action";
  anatomyAction.setAttribute("aria-expanded", "false");
  anatomyAction.innerHTML = '<span class="invivo-menu__nav-icon">☰</span><span>Anatomy</span>';

  const drapesAction = document.createElement("button");
  drapesAction.type = "button";
  drapesAction.className = "invivo-menu__nav-action";

  bottomBar.append(anatomyAction, drapesAction);
  shell.append(sheet, bottomBar);

  const setDrapesActionState = (): void => {
    drapesAction.classList.toggle("is-active", drapesVisible);
    drapesAction.innerHTML = `<span class="invivo-menu__nav-icon">▰</span><span>Drapes</span><span class="invivo-menu__nav-state">${drapesVisible ? "On" : "Off"}</span>`;
  };

  const setOpen = (open: boolean): void => {
    isOpen = open;
    sheet.hidden = !open;
    anatomyAction.classList.toggle("is-active", open);
    anatomyAction.setAttribute("aria-expanded", open ? "true" : "false");
  };

  const getCurrentView = (): MenuView => activeViews[activeViews.length - 1];

  const getNodeState = (node: InvivoMenuAnatomyNode): "checked" | "unchecked" | "indeterminate" => {
    const nodeIds: string[] = [];
    forEachNode([node], (candidate) => {
      nodeIds.push(candidate.id);
    });

    const selectedCount = nodeIds.filter((id) => visibility.get(id)).length;

    if (selectedCount === 0) {
      return "unchecked";
    }

    if (selectedCount === nodeIds.length) {
      return "checked";
    }

    return "indeterminate";
  };

  const setNodeSubtreeVisible = (node: InvivoMenuAnatomyNode, visible: boolean): void => {
    forEachNode([node], (candidate) => {
      visibility.set(candidate.id, visible);
    });

    const callback = onAnatomyNodeVisibleChange || onAnatomyGroupVisibleChange;
    callback?.(node.id, visible);
  };

  const renderSkinView = (): void => {
    const list = document.createElement("div");
    list.className = "invivo-menu__list";

    SKIN_TRANSLUCENCY_OPTIONS.forEach((option) => {
      const row = document.createElement("button");
      row.type = "button";
      row.className = "invivo-menu__row invivo-menu__row--option";

      const marker = document.createElement("span");
      marker.className = "invivo-menu__radio";
      marker.textContent = skinSetting === option.setting ? "●" : "○";

      const label = document.createElement("span");
      label.className = "invivo-menu__row-label";
      label.textContent = option.label;

      row.append(marker, label);
      row.addEventListener("click", () => {
        skinSetting = option.setting;
        lastVisibleSkinSetting = option.setting;
        onSkinChange(skinSetting);
        setOpen(false);
        render();
      });
      list.append(row);
    });

    sheet.append(list);
  };

  const renderAnatomyView = (node: InvivoMenuAnatomyNode | null): void => {
    const list = document.createElement("div");
    list.className = "invivo-menu__list";

    if (!node) {
      const skinRow = document.createElement("div");
      skinRow.className = "invivo-menu__row";

      const skinCheckbox = document.createElement("input");
      skinCheckbox.type = "checkbox";
      skinCheckbox.className = "invivo-menu__checkbox";
      skinCheckbox.checked = skinSetting !== "none";
      skinCheckbox.setAttribute("aria-label", "Toggle skin");
      skinCheckbox.addEventListener("click", (event) => {
        event.stopPropagation();
      });
      skinCheckbox.addEventListener("change", () => {
        skinSetting = skinCheckbox.checked ? lastVisibleSkinSetting : "none";
        onSkinChange(skinSetting);
        setOpen(false);
        render();
      });

      const skinNav = document.createElement("button");
      skinNav.type = "button";
      skinNav.className = "invivo-menu__row-nav";

      const skinLabel = document.createElement("span");
      skinLabel.className = "invivo-menu__row-label";
      skinLabel.textContent = "Skin";

      const skinState = document.createElement("span");
      skinState.className = "invivo-menu__row-state";
      skinState.textContent = skinSetting === "none" ? "Off" : getSkinLabel(skinSetting);

      const skinChevron = document.createElement("span");
      skinChevron.className = "invivo-menu__chevron";
      skinChevron.textContent = "›";

      skinNav.append(skinLabel, skinState, skinChevron);
      skinNav.addEventListener("click", () => {
        activeViews.push({ kind: "skin" });
        render();
      });
      skinRow.append(skinCheckbox, skinNav);
      list.append(skinRow);
    }

    const nodes = node ? node.children : tree;

    nodes.forEach((child) => {
      const row = document.createElement("div");
      row.className = "invivo-menu__row";

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.className = "invivo-menu__checkbox";

      const state = getNodeState(child);
      checkbox.checked = state === "checked";
      checkbox.indeterminate = state === "indeterminate";
      checkbox.setAttribute("aria-label", `Toggle ${child.label}`);
      checkbox.addEventListener("click", (event) => {
        event.stopPropagation();
      });
      checkbox.addEventListener("change", () => {
        setNodeSubtreeVisible(child, checkbox.checked);
        setOpen(false);
        render();
      });

      const nav = document.createElement("button");
      nav.type = "button";
      nav.className = "invivo-menu__row-nav";
      nav.disabled = child.children.length === 0;

      const label = document.createElement("span");
      label.className = "invivo-menu__row-label";
      label.textContent = child.label;
      nav.append(label);

      if (child.complication) {
        const warning = document.createElement("span");
        warning.className = "invivo-menu__warning";
        warning.textContent = "!";
        nav.append(warning);
      }

      if (child.children.length) {
        const chevron = document.createElement("span");
        chevron.className = "invivo-menu__chevron";
        chevron.textContent = "›";
        nav.append(chevron);
        nav.addEventListener("click", () => {
          activeViews.push({ kind: "anatomy", node: child });
          render();
        });
      } else {
        nav.addEventListener("click", () => {
          checkbox.checked = !checkbox.checked;
          setNodeSubtreeVisible(child, checkbox.checked);
          render();
        });
      }

      row.append(checkbox, nav);
      list.append(row);
    });

    sheet.append(list);
  };

  const render = (): void => {
    sheet.replaceChildren();

    const view = getCurrentView();
    const header = document.createElement("header");
    header.className = "invivo-menu__header";

    const backButton = document.createElement("button");
    backButton.type = "button";
    backButton.className = "invivo-menu__back";
    backButton.textContent = "‹";
    const canGoBack = activeViews.length > 1;
    backButton.setAttribute("aria-hidden", canGoBack ? "false" : "true");
    backButton.tabIndex = canGoBack ? 0 : -1;
    backButton.setAttribute("aria-label", "Back");
    backButton.addEventListener("click", () => {
      activeViews.pop();
      render();
    });

    const title = document.createElement("div");
    title.className = "invivo-menu__title";

    if (view.kind === "skin") {
      title.textContent = "Skin";
    } else {
      title.textContent = view.node?.label || sceneTitle;
    }

    header.append(backButton, title);
    sheet.append(header);

    const breadcrumb = document.createElement("div");
    breadcrumb.className = "invivo-menu__breadcrumb";
    const currentNode = view.kind === "anatomy" ? view.node : null;
    breadcrumb.textContent = currentNode ? findPath(tree, currentNode).map((item) => item.label).join(" / ") : "Top level";
    sheet.append(breadcrumb);

    if (view.kind === "skin") {
      renderSkinView();
    } else {
      renderAnatomyView(view.node);
    }
  };

  const anatomyActionHandler = (): void => {
    setOpen(!isOpen);
    render();
  };

  const drapesActionHandler = (): void => {
    drapesVisible = !drapesVisible;
    setDrapesActionState();
    onDrapesVisibleChange(drapesVisible);
  };

  const shellClickHandler = (event: MouseEvent): void => {
    event.stopPropagation();
  };

  const documentClickHandler = (): void => {
    setOpen(false);
  };

  setDrapesActionState();
  render();
  shell.addEventListener("click", shellClickHandler);
  anatomyAction.addEventListener("click", anatomyActionHandler);
  drapesAction.addEventListener("click", drapesActionHandler);
  document.addEventListener("click", documentClickHandler);
  parent.appendChild(shell);

  return {
    dispose(): void {
      shell.removeEventListener("click", shellClickHandler);
      anatomyAction.removeEventListener("click", anatomyActionHandler);
      drapesAction.removeEventListener("click", drapesActionHandler);
      document.removeEventListener("click", documentClickHandler);
      shell.remove();
    },
  };
}
